import type { Wall } from "@elektr/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Image as ImageIcon, Pencil, PencilRuler, Plus, Trash2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, EmptyState, Spinner } from "@/components/ui/card";
import { ConfirmDialog, Dialog } from "@/components/ui/dialog";
import { Field, Input, Select, Textarea } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, isConflict, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

import { usePropertyContext } from "./PropertyDetailPage";

const TEMPLATES = [
  { value: "studio", key: "tpl.studio" },
  { value: "1_room", key: "tpl.1_room" },
  { value: "2_room", key: "tpl.2_room" },
  { value: "3_room", key: "tpl.3_room" },
] as const;

type TreeWall = {
  id: string;
  name: string;
  order?: number;
  width_cm?: number | null;
  height_cm?: number | null;
  notes?: string;
  version?: number;
  has_scheme?: boolean;
};

export function StructureTab() {
  const { property } = usePropertyContext();
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const key = ["property-tree", property.id];

  const tree = useQuery({
    queryKey: key,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/properties/{id}/tree/", { params: { path: { id: property.id } } }),
      ),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const onError = (e: unknown) => toast.error(errorMessage(e, t("common.error")));

  const [roomName, setRoomName] = useState("");
  const [template, setTemplate] = useState<string>("2_room");
  const [wallDialog, setWallDialog] = useState<{ roomId: string; wall?: TreeWall } | null>(null);
  const [del, setDel] = useState<{ kind: "room" | "wall"; id: string; name: string } | null>(null);
  const [rename, setRename] = useState<{ id: string; name: string } | null>(null);

  const addRoom = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/rooms/", {
          body: {
            property: property.id,
            name: roomName.trim(),
            order: tree.data?.rooms.length ?? 0,
          },
        }),
      ),
    onSuccess: () => {
      setRoomName("");
      void refresh();
    },
    onError,
  });
  const applyTpl = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST("/api/properties/{id}/apply-template/", {
          params: { path: { id: property.id } },
          body: { template: template as "2_room" },
        }),
      ),
    onSuccess: () => {
      toast.success(t("common.saved"));
      void refresh();
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: async (d: { kind: "room" | "wall"; id: string }) => {
      const res =
        d.kind === "room"
          ? await api.DELETE("/api/rooms/{id}/", { params: { path: { id: d.id } } })
          : await api.DELETE("/api/walls/{id}/", { params: { path: { id: d.id } } });
      if (!res.response.ok) throw new Error("delete");
    },
    onSuccess: () => {
      setDel(null);
      void refresh();
    },
    onError,
  });
  const renameRoom = useMutation({
    mutationFn: async (r: { id: string; name: string }) =>
      unwrap(
        await api.PATCH("/api/rooms/{id}/", {
          params: { path: { id: r.id } },
          body: { name: r.name },
        }),
      ),
    onSuccess: () => {
      setRename(null);
      void refresh();
    },
    onError,
  });

  if (tree.isLoading) return <Spinner className="m-8 size-8" />;
  const rooms = tree.data?.rooms ?? [];

  return (
    <div className="flex flex-col gap-4">
      <Card className="grid gap-3 md:grid-cols-2">
        <form
          className="flex gap-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (roomName.trim()) addRoom.mutate();
          }}
        >
          <Input
            aria-label={t("struct.room_name")}
            placeholder={t("struct.room_name")}
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
          />
          <Button type="submit" disabled={addRoom.isPending || !roomName.trim()}>
            <Plus className="size-4" /> {t("struct.add_room")}
          </Button>
        </form>
        <div className="flex gap-2">
          <Select
            aria-label={t("struct.template")}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          >
            {TEMPLATES.map((x) => (
              <option key={x.value} value={x.value}>
                {t(x.key)}
              </option>
            ))}
          </Select>
          <Button variant="outline" disabled={applyTpl.isPending} onClick={() => applyTpl.mutate()}>
            {t("struct.apply_template")}
          </Button>
        </div>
      </Card>

      {rooms.length === 0 && <EmptyState>{t("struct.empty")}</EmptyState>}

      {rooms.map((room) => (
        <Card key={room.id}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-lg font-bold">{room.name}</h3>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("common.edit")}
                onClick={() => setRename({ id: room.id, name: room.name })}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t("common.delete")}
                onClick={() => setDel({ kind: "room", id: room.id, name: room.name })}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          </div>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {room.walls.map((w) => (
              <li key={w.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                <div>
                  <div className="font-medium">{w.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {w.width_cm && w.height_cm
                      ? `${w.width_cm} × ${w.height_cm} cm`
                      : t("struct.size_unknown")}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button asChild variant={w.has_scheme ? "outline" : "primary"} size="sm">
                    <Link to={`../scheme/${w.id}`.replace("..", `/properties/${property.id}`)}>
                      <PencilRuler className="size-4" />{" "}
                      {w.has_scheme ? t("struct.scheme_edit") : t("struct.scheme_draw")}
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm">
                    <Link to={`../media?wall=${w.id}`}>
                      <ImageIcon className="size-4" /> {t("tab.media")}
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("common.edit")}
                    onClick={() => setWallDialog({ roomId: room.id, wall: w })}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("common.delete")}
                    onClick={() => setDel({ kind: "wall", id: w.id, name: w.name })}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </li>
            ))}
            {room.walls.length === 0 && (
              <li className="p-3 text-sm text-muted-foreground">{t("struct.no_walls")}</li>
            )}
          </ul>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => setWallDialog({ roomId: room.id })}
          >
            <Plus className="size-4" /> {t("struct.add_wall")}
          </Button>
        </Card>
      ))}

      {wallDialog && (
        <WallDialog
          key={wallDialog.wall?.id ?? "new"}
          roomId={wallDialog.roomId}
          wall={wallDialog.wall}
          nextOrder={rooms.find((r) => r.id === wallDialog.roomId)?.walls.length ?? 0}
          onClose={() => setWallDialog(null)}
          onSaved={() => {
            setWallDialog(null);
            void refresh();
          }}
        />
      )}

      <Dialog
        open={!!rename}
        onOpenChange={(o) => !o && setRename(null)}
        title={t("struct.rename_room")}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (rename?.name.trim()) renameRoom.mutate({ id: rename.id, name: rename.name.trim() });
          }}
        >
          <Field label={t("struct.room_name")}>
            {(id) => (
              <Input
                id={id}
                value={rename?.name ?? ""}
                onChange={(e) => setRename((r) => (r ? { ...r, name: e.target.value } : r))}
              />
            )}
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRename(null)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={renameRoom.isPending}>
              {t("common.save")}
            </Button>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title={t(del?.kind === "room" ? "struct.delete_room" : "struct.delete_wall")}
        message={t("struct.delete_message", { name: del?.name ?? "" })}
        confirmLabel={t("common.delete")}
        pending={remove.isPending}
        onConfirm={() => del && remove.mutate(del)}
      />
    </div>
  );
}

function WallDialog({
  roomId,
  wall,
  nextOrder,
  onClose,
  onSaved,
}: {
  roomId: string;
  wall?: TreeWall;
  nextOrder: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const [name, setName] = useState(wall?.name ?? "");
  const [width, setWidth] = useState(wall?.width_cm ? String(wall.width_cm) : "");
  const [height, setHeight] = useState(wall?.height_cm ? String(wall.height_cm) : "");
  const [notes, setNotes] = useState(wall?.notes ?? "");
  const [conflict, setConflict] = useState(false);

  const save = useMutation({
    mutationFn: async () => {
      const dims = {
        width_cm: width ? Number(width) : null,
        height_cm: height ? Number(height) : null,
      };
      if (wall) {
        return unwrap(
          await api.PATCH("/api/walls/{id}/", {
            params: { path: { id: wall.id } },
            body: { name: name.trim(), notes, ...dims, version: wall.version },
          }),
        );
      }
      return unwrap(
        await api.POST("/api/walls/", {
          body: { room: roomId, name: name.trim(), notes, order: nextOrder, ...dims },
        }),
      );
    },
    onSuccess: onSaved,
    onError: (e) =>
      isConflict(e) ? setConflict(true) : toast.error(errorMessage(e, t("common.error"))),
  });

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={wall ? t("struct.edit_wall") : t("struct.add_wall")}
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        {conflict && (
          <p role="alert" className="rounded-lg bg-warning/15 p-3 text-sm">
            {t("common.conflict")}
          </p>
        )}
        <Field label={t("struct.wall_name")} hint={t("struct.wall_name_hint")}>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("struct.width_cm")}>
            {(id) => (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={1}
                value={width}
                onChange={(e) => setWidth(e.target.value)}
              />
            )}
          </Field>
          <Field label={t("struct.height_cm")}>
            {(id) => (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={1}
                value={height}
                onChange={(e) => setHeight(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label={t("props.notes")}>
          {(id) => (
            <Textarea id={id} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          )}
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={save.isPending || !name.trim()}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

export type { Wall };
